import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router';
import { useAuth } from './context/AuthContext';
import { Layout } from './components/layout/Layout';
import { AdminLayout } from './components/layout/AdminLayout';
import { PageLoader } from './components/ui/Spinner';

// Lazy load pages for better performance
const HomePage = lazy(() => import('./pages/customer/HomePage'));
const MenuPage = lazy(() => import('./pages/customer/MenuPage'));
const MenuItemPage = lazy(() => import('./pages/customer/MenuItemPage'));
const CartPage = lazy(() => import('./pages/customer/CartPage'));
const LoginPage = lazy(() => import('./pages/customer/LoginPage'));
const RegisterPage = lazy(() => import('./pages/customer/RegisterPage'));
const OrderTrackingPage = lazy(() => import('./pages/customer/OrderTrackingPage'));
const OrdersHistoryPage = lazy(() => import('./pages/customer/OrdersHistoryPage'));
const LocationPage = lazy(() => import('./pages/customer/LocationPage'));
const ProfilePage = lazy(() => import('./pages/customer/ProfilePage'));

// Admin pages
const AdminDashboard = lazy(() => import('./pages/owner/AdminDashboard'));
const AdminOrdersPage = lazy(() => import('./pages/owner/AdminOrdersPage'));
const AdminMenuPage = lazy(() => import('./pages/owner/AdminMenuPage'));
const AdminStatsPage = lazy(() => import('./pages/owner/AdminStatsPage'));
// LOYALTY DISABLED - const AdminLoyaltyPage = lazy(() => import('./pages/owner/AdminLoyaltyPage'));
const AdminNotificationsPage = lazy(() => import('./pages/owner/AdminNotificationsPage'));
const AdminSettingsPage = lazy(() => import('./pages/owner/AdminSettingsPage'));
const AdminCustomizationsPage = lazy(() => import('./pages/owner/AdminCustomizationsPage'));
const AdminLocationsPage = lazy(() => import('./pages/owner/AdminLocationsPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

// Protected route wrapper
const ProtectedRoute: React.FC<{ children: React.ReactNode; requireAuth?: boolean; requireOwner?: boolean }> = ({
  children,
  requireAuth = false,
  requireOwner = false,
}) => {
  const { isAuthenticated, user, isLoading } = useAuth();

  if (isLoading) {
    return <PageLoader />;
  }

  if (requireAuth && !isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requireOwner && user?.role !== 'owner') {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Auth pages (no layout) */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />

        {/* Customer pages (with layout) */}
        <Route element={<Layout />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/menu" element={<MenuPage />} />
          <Route path="/menu/:id" element={<MenuItemPage />} />
          <Route path="/panier" element={<CartPage />} />
          <Route path="/localisation" element={<LocationPage />} />
          <Route path="/profil" element={<ProfilePage />} />
          {/* LOYALTY DISABLED - <Route path="/fidelite" element={<ProfilePage />} /> */}

          {/* Protected customer routes */}
          <Route
            path="/commande/:id"
            element={
              <ProtectedRoute requireAuth>
                <OrderTrackingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/commandes"
            element={
              <ProtectedRoute requireAuth>
                <OrdersHistoryPage />
              </ProtectedRoute>
            }
          />
        </Route>

        {/* Admin pages */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute requireOwner>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<AdminDashboard />} />
          <Route path="commandes" element={<AdminOrdersPage />} />
          <Route path="commandes/:id" element={<AdminOrdersPage />} />
          <Route path="menu" element={<AdminMenuPage />} />
          <Route path="personnalisations" element={<AdminCustomizationsPage />} />
          <Route path="emplacements" element={<AdminLocationsPage />} />
          <Route path="stats" element={<AdminStatsPage />} />
          {/* LOYALTY DISABLED - <Route path="fidelite" element={<AdminLoyaltyPage />} /> */}
          <Route path="notifications" element={<AdminNotificationsPage />} />
          <Route path="parametres" element={<AdminSettingsPage />} />
        </Route>

        {/* Catch all - 404 page */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
};

export default App;
